import { normalizeError } from './normalizeError';

/**
 * Recovery from stale Vite chunks after a frontend deploy (SP-1330).
 *
 * Vite emits content-hashed chunks. A deploy replaces those files, but an
 * already-open tab keeps the old import graph in memory, so the next lazy route
 * requests a hash that no longer exists. Instead of dropping the user on the
 * generic error fallback, we fetch a fresh document once and let the new bundle
 * take over.
 */

/** sessionStorage key holding the timestamp of the last recovery attempt. */
const STALE_CHUNK_STORAGE_KEY = 'slc.staleChunkReloadAt';

/** Query param appended to force a new document instead of a cached one. */
const STALE_CHUNK_QUERY_PARAM = '_stale';

/**
 * A second failure this soon after a recovery attempt means the freshly loaded
 * document is broken too, so we stop reloading and show the normal fallback.
 * Once the window passes, a later deploy in the same tab can recover again.
 */
const STALE_CHUNK_COOLDOWN_MS = 30_000;

const STALE_CHUNK_PATTERNS = [
  // Chrome / Edge / Safari dynamic import failures
  /failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  // Firefox
  /importing a module script failed/i,
  // Module script served with the SPA fallback (text/html) instead of JS
  /failed to load module script/i,
  /expected a javascript(?:-or-wasm)? module script/i,
  /is not a valid javascript mime type/i,
  // Vite / bundler chunk helpers
  /loading chunk \S+ failed/i,
  /loading css chunk \S+ failed/i,
  /unable to preload css/i,
];

const isChunkLoadErrorName = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  'name' in error &&
  (error as { name?: unknown }).name === 'ChunkLoadError';

/**
 * True when the error looks like a missing/stale build artifact rather than an
 * application bug.
 */
export const isStaleChunkError = (error: unknown): boolean => {
  if (error === null || error === undefined) {
    return false;
  }

  if (isChunkLoadErrorName(error)) {
    return true;
  }

  const message = normalizeError(error);

  return STALE_CHUNK_PATTERNS.some((pattern) => pattern.test(message));
};

const readLastAttempt = (): number | null => {
  try {
    const raw = window.sessionStorage.getItem(STALE_CHUNK_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Private mode / storage disabled: treated as "no record".
    return null;
  }
};

/** Returns false when the write fails, so callers know the guard is unusable. */
const writeLastAttempt = (timestamp: number): boolean => {
  try {
    window.sessionStorage.setItem(STALE_CHUNK_STORAGE_KEY, String(timestamp));
    return true;
  } catch {
    return false;
  }
};

const currentUrlIsRecoveryAttempt = (): boolean => {
  try {
    return new URL(window.location.href).searchParams.has(
      STALE_CHUNK_QUERY_PARAM
    );
  } catch {
    return false;
  }
};

/**
 * Fetch a fresh document once so the browser picks up the current index.html
 * and its new chunk hashes.
 *
 * Guards against a reload loop two ways: the sessionStorage cooldown, and the
 * `_stale` marker still present on the current URL (which covers tabs where
 * sessionStorage is unavailable).
 *
 * `location.reload()` is deliberately not used: StackTwin's nginx origin serves
 * index.html without Cache-Control, so a soft reload can replay the same stale
 * HTML.
 *
 * @returns true when a document load was triggered.
 */
export const reloadOnceForStaleChunk = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (currentUrlIsRecoveryAttempt()) {
    return false;
  }

  const now = Date.now();
  const lastAttempt = readLastAttempt();

  if (lastAttempt !== null && now - lastAttempt < STALE_CHUNK_COOLDOWN_MS) {
    return false;
  }

  writeLastAttempt(now);

  try {
    const target = new URL(window.location.href);
    target.searchParams.set(STALE_CHUNK_QUERY_PARAM, String(now));
    window.location.replace(target.toString());
    return true;
  } catch {
    return false;
  }
};

/**
 * Called once the app has booted successfully. Drops the `_stale` marker from
 * the address bar so the user never sees it and so the URL stays shareable.
 *
 * The marker is only removed when the sessionStorage cooldown is actually
 * usable; otherwise it stays as the sole loop guard for this tab.
 */
export const completeStaleChunkRecovery = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (!currentUrlIsRecoveryAttempt()) {
    return;
  }

  const storageUsable = writeLastAttempt(readLastAttempt() ?? Date.now());
  if (!storageUsable) {
    return;
  }

  try {
    const target = new URL(window.location.href);
    target.searchParams.delete(STALE_CHUNK_QUERY_PARAM);
    window.history.replaceState(
      window.history.state,
      '',
      `${target.pathname}${target.search}${target.hash}`
    );
  } catch {
    // Leaving the marker in place is harmless.
  }
};

export const STALE_CHUNK_TEST_CONSTANTS = {
  STALE_CHUNK_STORAGE_KEY,
  STALE_CHUNK_QUERY_PARAM,
  STALE_CHUNK_COOLDOWN_MS,
} as const;
